import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Building2, Users as UsersIcon, CreditCard, Activity, ArrowLeft, Shield, Bug, Sun, Moon } from 'lucide-react';
import SkillDeckLogo from './SkillDeckLogo';
import { getStoredTheme, toggleStoredTheme } from '../lib/theme';

const ADMIN_NAV = [
  { label: "Vue d'ensemble", icon: LayoutDashboard, path: '/admin' },
  { label: 'Organisations',  icon: Building2,       path: '/admin/orgs' },
  { label: 'Utilisateurs',   icon: UsersIcon,       path: '/admin/users' },
  { label: 'Paiements',      icon: CreditCard,      path: '/admin/payments' },
  { label: 'Activité',       icon: Activity,        path: '/admin/activity' },
  { label: 'Bug reports',    icon: Bug,             path: '/admin/bug-reports' },
];

const AdminLayout = ({ children, title }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [theme, setTheme] = useState(getStoredTheme);
  const toggleTheme = () => setTheme(toggleStoredTheme());

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="admin-shell min-h-screen bg-[#05050f] flex">
      {/* Sidebar */}
      <aside className="w-60 bg-[#0a0a1e] border-r border-[rgba(108,71,255,.18)] hidden lg:flex flex-col">
        <div className="flex items-center px-5 py-5 border-b border-[rgba(108,71,255,.15)]">
          <SkillDeckLogo size={32} />
          <div className="ml-3">
            <span className="text-base font-bold text-white">Skilldeck</span>
            <span className="block text-[10px] font-semibold text-violet-300 uppercase tracking-wider">Console admin</span>
          </div>
        </div>

        <div className="px-4 py-3 mx-3 mt-3 rounded-lg border border-[rgba(108,71,255,.2)] bg-[rgba(108,71,255,.06)]">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-3.5 w-3.5 text-violet-300" />
            <p className="text-[10px] font-semibold text-violet-300 uppercase tracking-wider">Super admin</p>
          </div>
          <p className="text-xs text-gray-400 truncate">{user?.email}</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {ADMIN_NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${active
                    ? 'bg-[rgba(108,71,255,.18)] text-white'
                    : 'text-gray-400 hover:bg-[rgba(108,71,255,.08)] hover:text-gray-100'}
                `}
              >
                <Icon className={`h-4.5 w-4.5 ${active ? 'text-violet-300' : 'text-gray-500'}`} style={{ width: 18, height: 18 }} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="border-t border-[rgba(108,71,255,.15)] px-3 py-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:bg-[rgba(108,71,255,.08)] hover:text-gray-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 text-gray-500" />
            <span>Retour à l'app</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-[#0a0a1e]/90 backdrop-blur-lg border-b border-[rgba(108,71,255,.15)] sticky top-0 z-30">
          <div className="flex items-center px-4 sm:px-6 py-4 gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="lg:hidden text-gray-400 hover:text-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <Shield className="h-4 w-4 text-violet-300 lg:hidden" />
            {title && <h1 className="text-lg sm:text-xl font-semibold text-white">{title}</h1>}
            <button
              onClick={toggleTheme}
              title={theme === 'light' ? 'Passer en mode sombre' : 'Passer en mode clair'}
              className="ml-auto flex items-center justify-center h-8 w-8 rounded-lg border border-[rgba(108,71,255,.2)] text-gray-400 hover:text-gray-100 hover:bg-[rgba(108,71,255,.08)] transition-colors"
            >
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </button>
          </div>
        </header>

        <main key={location.pathname} className="flex-1 overflow-y-auto sd-page-anim">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
